'use client';

import Icon from '@/components/ui/AppIcon';

interface Guideline {
  title: string;
  description: string;
  rules: string[];
  icon: string;
}

export default function GuestGuidelinesSection() {
  const guidelines: Guideline[] = [
    {
      title: 'Property Care',
      description: 'Guests are responsible for maintaining the apartment in good condition during their stay.',
      icon: 'HomeModernIcon',
      rules: [
        'Report any damages or maintenance issues immediately',
        'Use appliances and amenities as intended',
        'No smoking inside apartments (designated areas available)',
        'Keep apartment reasonably clean and tidy',
        'Dispose of garbage in designated bins',
      ],
    },
    {
      title: 'Noise & Conduct',
      description: 'Maintain peaceful environment respecting other guests and neighbors.',
      icon: 'SpeakerXMarkIcon',
      rules: [
        'Quiet hours: 10:00 PM to 7:00 AM daily',
        'Keep noise levels reasonable at all times',
        'No parties or large gatherings without prior approval',
        'Respect common areas and fellow guests',
        'Follow staff instructions and property rules',
      ],
    },
    {
      title: 'Visitor Policy',
      description: 'Visitors are strictly managed to ensure security and comfort for all guests.',
      icon: 'UserGroupIcon',
      rules: [
        'Visitors are not permitted in guest rooms',
        'All visitors must meet only in the lobby or designated areas',
        'Prior approval from the Front Desk is required for any visitor',
        'The management reserves the right to deny entry or request visitors to leave without prior notice',
        'Registered guest is responsible for visitor conduct',
      ],
    },
    {
      title: 'Security & Safety',
      description: 'Follow safety protocols to ensure secure environment for all guests.',
      icon: 'ShieldCheckIcon',
      rules: [
        'Keep apartment locked when leaving',
        'Do not share access codes or keys',
        'Report suspicious activity to security immediately',
        'Follow fire safety procedures and evacuation routes',
        'Do not tamper with safety equipment (alarms, extinguishers)',
      ],
    },
    {
      title: 'Damage Liability',
      description: 'Guests are financially responsible for damages beyond normal wear and tear.',
      icon: 'ExclamationTriangleIcon',
      rules: [
        'Charges for damages deducted from security deposit',
        'Replacement cost charged for missing items',
        'Accidental damage must be reported immediately',
        'Photographic evidence maintained for all damage claims',
        'Dispute resolution through management review',
      ],
    },
    {
      title: 'Prohibited Activities',
      description: 'Certain activities are strictly prohibited to ensure safety and compliance.',
      icon: 'NoSymbolIcon',
      rules: [
        'No illegal activities or substances',
        'No weapons or dangerous items',
        'No unauthorized commercial activities',
        'No pets without prior approval and pet fee',
        'No smoking inside rooms, bathrooms, or any indoor areas — violation attracts a cleaning and restoration charge of Rs. 3,000',
      ],
    },
  ];

  return (
    <section id="guest-guidelines" className="py-16 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="ClipboardDocumentListIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Guest Guidelines</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Guest Responsibilities
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Essential guidelines covering property care, noise policies, visitor restrictions, security protocols, and damage liability.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {guidelines.map((guideline, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-6 border border-border hover:border-primary hover:shadow-elevated transition-all duration-300"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center mb-4">
                <Icon name={guideline.icon as any} size={28} className="text-white" />
              </div>
              <h3 className="font-display text-xl font-semibold text-primary mb-2">
                {guideline.title}
              </h3>
              <p className="text-sm text-text-secondary mb-4 leading-relaxed">
                {guideline.description}
              </p>
              <ul className="space-y-2">
                {guideline.rules.map((rule, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={16}
                      className="text-accent flex-shrink-0 mt-0.5"
                    />
                    <span className="text-xs text-text-secondary leading-relaxed">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-muted rounded-2xl p-8 lg:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <Icon name="HandThumbUpIcon" size={48} className="text-accent mx-auto mb-6" />
            <h3 className="font-display text-2xl font-bold text-primary mb-4">
              Thank You for Your Cooperation
            </h3>
            <p className="text-text-secondary leading-relaxed mb-6">
              These guidelines help us maintain a safe, comfortable, and enjoyable environment for all guests. By following these policies, you contribute to the exceptional experience that Proximus is known for. If you have questions about any guideline, our team is always available to assist.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-us"
                className="px-8 py-3 bg-primary text-white font-semibold rounded-lg shadow-subtle hover:shadow-elevated hover:bg-primary/90 transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-white" />
                <span>Contact Us</span>
              </a>
              <a
                href="https://bookingengine.stayflexi.com/?hotel_id=33526"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-accent text-white font-semibold rounded-lg shadow-subtle hover:shadow-elevated hover:bg-accent/90 transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Icon name="CalendarIcon" size={20} className="text-white" />
                <span>Book Your Stay</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}